import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';

import { Button } from 'primereact/button';
import { confirmDialog } from 'primereact/confirmdialog';
import { Message } from 'primereact/message';
import { Toast } from 'primereact/toast';

import { useShift } from '../../context/ShiftProvider';
import useAuthenticatedRequest from '../../utils/TokenRefreshUtils';
import useValidateShift from '../../utils/ShiftUtils';
import { useIsDesktop } from '../../utils/useMediaQuery';
import {
    displayDate,
    formatCurrency,
    formatShortDate,
    pluralize,
} from '../../utils/CommonUtils';

import Card from '../common/Card';
import Drawer from '../common/Drawer';
import EmptyState from '../common/EmptyState';
import Money from '../common/Money';
import PaymentEditor, { UndistributedMessage } from '../common/PaymentEditor';
import RecordList from '../common/RecordList';
import SummaryList, { SummaryRow } from '../common/SummaryList';

const PAGE_SIZE = 10;

/**
 * Незакриті замовлення: список чернеток актів, їх деталі, оплата й видалення.
 *
 * Було: дві таблиці по пів екрана (col-6) навіть на телефоні — на 375px
 * кожна колонка мала 160px, і текст переносився по одному слову.
 * Стало: на телефоні — список на всю ширину, деталі відкриваються панеллю;
 * на десктопі — класичний майстер-детейл.
 */
const OpenSales = () => {
    const authenticatedRequest = useAuthenticatedRequest();
    const validateShift = useValidateShift();
    const navigate = useNavigate();
    const location = useLocation();
    const toast = useRef(null);
    const { isShiftOpen } = useShift();
    const isDesktop = useIsDesktop();

    const [sales, setSales] = useState([]);
    const [page, setPage] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loading, setLoading] = useState(true);
    const [reloadToken, setReloadToken] = useState(0);

    const [clients, setClients] = useState([]);
    const [currencyRates, setCurrencyRates] = useState(null);

    const [selectedSale, setSelectedSale] = useState(null);
    const [detailsVisible, setDetailsVisible] = useState(false);
    const [paymentVisible, setPaymentVisible] = useState(false);
    const [payments, setPayments] = useState([]);
    const [paymentErrors, setPaymentErrors] = useState([]);
    const [totalSumUAH, setTotalSumUAH] = useState(0);
    const [busy, setBusy] = useState(false);

    const reload = useCallback(() => setReloadToken((value) => value + 1), []);

    useEffect(() => {
        if (location.state?.toast) {
            toast.current?.show(location.state.toast);
            window.history.replaceState({}, document.title);
        }
    }, [location.state]);

    // Довідник клієнтів — щоб показати ім'я замість числового id.
    // TODO(backend): додати ім'я клієнта в get_unfinalized_acts,
    // тоді цей запит на 500 записів стане непотрібним.
    useEffect(() => {
        const fetchClients = async () => {
            const url = `${process.env.REACT_APP_BACKEND_URL}cagents/get_clients/?page=1&page_size=500`;
            const options = { method: 'GET', headers: { 'Content-Type': 'application/json' } };
            const response = await authenticatedRequest(url, options);

            if (response?.status === 200) {
                const parsed = await response.json();
                setClients(parsed.results ?? []);
            }
        };

        fetchClients();
    }, [authenticatedRequest]);

    useEffect(() => {
        const fetchRates = async () => {
            const url = `${process.env.REACT_APP_BACKEND_URL}currency/get_currencies_list/`;
            const options = { method: 'GET', headers: { 'Content-Type': 'application/json' } };
            const response = await authenticatedRequest(url, options);

            if (response?.status === 200) {
                const parsed = await response.json();
                setCurrencyRates(
                    parsed.reduce((acc, item) => {
                        acc[item.currency_short] = item.currency_rate;
                        return acc;
                    }, {})
                );
            }
        };

        fetchRates();
    }, [authenticatedRequest]);

    useEffect(() => {
        let cancelled = false;

        const fetchSales = async () => {
            setLoading(true);

            const url = `${process.env.REACT_APP_BACKEND_URL}kasa_shifts/get_unfinalized_acts/?page=${page}&page_size=${PAGE_SIZE}&ordering=-act_date`;
            const options = { method: 'GET', headers: { 'Content-Type': 'application/json' } };

            try {
                const response = await authenticatedRequest(url, options);
                const parsed = await response.json();

                if (!cancelled) {
                    setSales(parsed.results ?? []);
                    setTotalRecords(parsed.count ?? 0);
                }
            } catch (error) {
                console.error('Не вдалося отримати незакриті замовлення', error);
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        fetchSales();

        return () => {
            cancelled = true;
        };
    }, [authenticatedRequest, page, reloadToken]);

    const clientName = useCallback(
        (id) => clients.find((client) => client.id === id)?.name ?? 'Клієнта не вказано',
        [clients]
    );

    const undistributed = useMemo(() => {
        if (!currencyRates) {
            return 0;
        }

        const paid = payments.reduce(
            (sum, payment) => sum + Number(payment.sum) * (currencyRates[payment.currency] ?? 0),
            0
        );

        return totalSumUAH - paid;
    }, [payments, currencyRates, totalSumUAH]);

    const calculateTotals = (products) => {
        const totals = {};

        products.forEach((product) => {
            const quantity = product.quantity ?? 1;
            const currency = product.currency;

            if (!totals[currency]) {
                totals[currency] = { currency, sum: 0, paymentMethod: null };
            }

            totals[currency].sum += product.price * quantity;
        });

        return Object.values(totals);
    };

    const openSaleDetails = async (sale) => {
        // На десктопі деталі — друга колонка, панель відкривати не треба
        if (!isDesktop) {
            setDetailsVisible(true);
        }

        setSelectedSale({ id: sale.id, loading: true });

        const url = `${process.env.REACT_APP_BACKEND_URL}sale_acts/get_details/${sale.id}/`;
        const options = { method: 'GET', headers: { 'Content-Type': 'application/json' } };
        const response = await authenticatedRequest(url, options);

        if (response?.status !== 200) {
            toast.current?.show({
                severity: 'error',
                summary: 'Не вдалося отримати замовлення',
                life: 3000,
            });
            setSelectedSale(null);
            setDetailsVisible(false);
            return;
        }

        const parsed = await response.json();

        const products = [
            ...(parsed.identified_products ?? []),
            ...(parsed.unidentified_products ?? []),
            ...(parsed.services ?? []),
        ];

        const totals = calculateTotals(products);

        // Знижка зменшує суму до оплати у відповідній валюті
        (parsed.discount ?? []).forEach((discount) => {
            const total = totals.find((item) => item.currency === discount.currency);

            if (total) {
                total.sum -= Number(discount.amount) || 0;
            }
        });

        const detail = {
            id: sale.id,
            division: parsed.division,
            act_date: parsed.act_date,
            act_number: parsed.act_number,
            pay_due_date: parsed.pay_due_date,
            cagent: sale.cagent ? { id: sale.cagent, name: clientName(sale.cagent) } : null,
            products,
            discount: parsed.discount ?? [],
            totals,
        };

        setSelectedSale(detail);
        setPayments(totals.map((total) => ({ ...total })));
        setPaymentErrors([]);
        setTotalSumUAH(
            totals.reduce(
                (sum, total) => sum + total.sum * (currencyRates?.[total.currency] ?? 0),
                0
            )
        );
    };

    const submitPayment = async () => {
        const errors = payments.map((payment) =>
            payment.paymentMethod ? {} : { paymentMethod: 'Вкажіть спосіб оплати' }
        );

        setPaymentErrors(errors);

        if (!payments.length || errors.some((error) => error.paymentMethod)) {
            return;
        }

        setBusy(true);

        try {
            if (!(await validateShift())) {
                navigate('/shift', {
                    state: {
                        toast: {
                            severity: 'error',
                            summary: 'Зміна закрита',
                            detail: 'Оплата неможлива',
                            life: 4000,
                        },
                    },
                });
                return;
            }

            const finalizeUrl = `${process.env.REACT_APP_BACKEND_URL}sale_acts/make_finalized/${selectedSale.id}/`;
            const finalizeResponse = await authenticatedRequest(finalizeUrl, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
            });

            if (finalizeResponse?.status !== 200) {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Помилка фіналізації акту',
                    detail: `Код помилки: ${finalizeResponse?.status ?? 'невідомо'}`,
                    life: 4000,
                });
                return;
            }

            const paymentRequest = payments.map((payment) => ({
                act_id: selectedSale.id,
                date: formatShortDate(Date.now()),
                sum: payment.sum,
                currency: payment.currency,
                type: payment.paymentMethod,
                method: 'INCOME',
                cagent_id: selectedSale.cagent?.id ?? null,
                shift_id: Cookies.get('ShiftId'),
            }));

            const paymentResponse = await authenticatedRequest(
                `${process.env.REACT_APP_BACKEND_URL}accounting_payments/create/`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(paymentRequest),
                }
            );

            if (paymentResponse?.status === 201) {
                navigate('/shift', {
                    state: {
                        toast: {
                            severity: 'success',
                            summary: 'Оплату проведено',
                            life: 3000,
                        },
                    },
                });
            } else {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Помилка створення платежу',
                    detail: `Код помилки: ${paymentResponse?.status ?? 'невідомо'}`,
                    life: 4000,
                });
            }
        } finally {
            setBusy(false);
        }
    };

    const confirmDelete = () => {
        confirmDialog({
            header: 'Видалення замовлення',
            message: `Видалити замовлення №${selectedSale.id}? Дію не можна скасувати.`,
            icon: 'pi pi-trash',
            acceptLabel: 'Видалити',
            rejectLabel: 'Скасувати',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                const url = `${process.env.REACT_APP_BACKEND_URL}sale_acts/delete/${selectedSale.id}/`;
                const response = await authenticatedRequest(url, {
                    method: 'DELETE',
                    headers: { 'Content-Type': 'application/json' },
                });

                if (response?.status === 204) {
                    toast.current?.show({
                        severity: 'success',
                        summary: 'Замовлення видалено',
                        life: 3000,
                    });
                    setSelectedSale(null);
                    setDetailsVisible(false);
                    reload();
                } else {
                    toast.current?.show({
                        severity: 'error',
                        summary: 'Не вдалося видалити',
                        detail: `Код помилки: ${response?.status ?? 'невідомо'}`,
                        life: 4000,
                    });
                }
            },
        });
    };

    const productName = (item) => item.product ?? item.service ?? 'Позиція';

    const productMeta = (item) => {
        if (item.identifier) {
            return `IMEI ${item.identifier}`;
        }

        if (item.quantity !== undefined) {
            return `Кількість: ${item.quantity}`;
        }

        return 'Послуга';
    };

    const discountLabel = () => {
        const active = (selectedSale?.discount ?? []).filter((item) => Number(item.amount) > 0);

        if (!active.length) {
            return 'Без знижки';
        }

        return `Знижка: ${active
            .map((item) => formatCurrency(item.amount, item.currency))
            .join(', ')}`;
    };

    const detailsContent = selectedSale?.loading ? (
        <EmptyState icon="pi pi-spin pi-spinner" text="Завантаження замовлення…" />
    ) : selectedSale ? (
        <>
            <SummaryList>
                <SummaryRow label="Клієнт" value={selectedSale.cagent?.name ?? 'Не вказано'} />
                <SummaryRow label="Дата" value={displayDate(selectedSale.act_date)} />
                {selectedSale.act_number && (
                    <SummaryRow label="Номер акту" value={selectedSale.act_number} />
                )}
            </SummaryList>

            <div className="rc-card__header" style={{ borderTop: '1px solid var(--rc-border)' }}>
                <h2 className="rc-card__title">
                    {pluralize(selectedSale.products.length, [
                        'позиція',
                        'позиції',
                        'позицій',
                    ])}
                </h2>
            </div>

            {selectedSale.products.map((item, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <div className="rc-line-item" key={`${productName(item)}-${index}`}>
                    <div className="rc-line-item__main">
                        <div className="rc-line-item__name">{productName(item)}</div>
                        <div className="rc-line-item__meta">
                            <i className="pi pi-tag" />
                            <span>{productMeta(item)}</span>
                        </div>
                    </div>
                    <div className="rc-line-item__side">
                        <Money value={item.price} currency={item.currency} />
                    </div>
                </div>
            ))}

            <SummaryList>
                {selectedSale.totals.map((total) => (
                    <SummaryRow key={`total-${total.currency}`} label="До оплати" total>
                        <Money value={total.sum} currency={total.currency} />
                    </SummaryRow>
                ))}
            </SummaryList>
        </>
    ) : (
        <EmptyState
            icon="pi pi-clipboard"
            title="Замовлення не вибране"
            text="Виберіть замовлення зі списку, щоб побачити деталі"
        />
    );

    const detailsActions = selectedSale && !selectedSale.loading && (
        <>
            {!isShiftOpen && (
                <Message
                    className="rc-message"
                    severity="warn"
                    text="Зміна закрита — оплата й редагування недоступні"
                />
            )}
            <Button
                label="Оплатити замовлення"
                icon="pi pi-credit-card"
                className="w-full"
                disabled={!isShiftOpen}
                onClick={() => setPaymentVisible(true)}
            />
            <Button
                label="Змінити замовлення"
                icon="pi pi-pencil"
                severity="secondary"
                outlined
                className="w-full"
                disabled={!isShiftOpen}
                onClick={() => navigate('/newsale', { state: { sale: selectedSale } })}
            />
            <Button
                label="Видалити"
                icon="pi pi-trash"
                severity="danger"
                text
                className="w-full"
                onClick={confirmDelete}
            />
        </>
    );

    return (
        <>
            <Toast ref={toast} position="top-center" />

            <div className="rc-stack">
                <div>
                    <h1 className="rc-page-title">Незакриті замовлення</h1>
                    <p className="rc-page-subtitle">
                        {totalRecords > 0
                            ? `Всього ${pluralize(totalRecords, [
                                  'чернетка',
                                  'чернетки',
                                  'чернеток',
                              ])}`
                            : 'Чернетки актів, які ще не оплачені'}
                    </p>
                </div>

                <div className="rc-split">
                    <div className="rc-split__aside">
                        <Card flush>
                            <RecordList
                                items={sales}
                                loading={loading}
                                page={page}
                                pageSize={PAGE_SIZE}
                                totalRecords={totalRecords}
                                onPageChange={setPage}
                                emptyIcon="pi pi-check-circle"
                                emptyTitle="Незакритих замовлень немає"
                                emptyText="Усі акти оплачені або ще не створені"
                                renderItem={(sale) => (
                                    <li key={sale.id}>
                                        <button
                                            type="button"
                                            className={`rc-list-item ${
                                                selectedSale?.id === sale.id
                                                    ? 'rc-list-item--active'
                                                    : ''
                                            }`.trim()}
                                            onClick={() => openSaleDetails(sale)}
                                        >
                                            <div className="rc-list-item__main">
                                                <div className="rc-list-item__title">
                                                    №{sale.id} · {displayDate(sale.act_date)}
                                                </div>
                                                <div className="rc-list-item__meta">
                                                    <i className="pi pi-user" />
                                                    <span>{clientName(sale.cagent)}</span>
                                                </div>
                                            </div>
                                            <div className="rc-list-item__side">
                                                <Money value={sale.amountUAH} currency="UAH" />
                                                <i className="pi pi-chevron-right" />
                                            </div>
                                        </button>
                                    </li>
                                )}
                            />
                        </Card>
                    </div>

                    {/* Деталі: на десктопі — друга колонка, на телефоні — панель */}
                    <div className="rc-split__detail--mobile-hidden">
                        <Card
                            title={
                                selectedSale && !selectedSale.loading
                                    ? `Замовлення №${selectedSale.id}`
                                    : 'Деталі замовлення'
                            }
                            flush
                        >
                            {detailsContent}
                            {detailsActions && (
                                <div className="rc-drawer__footer">{detailsActions}</div>
                            )}
                        </Card>
                    </div>
                </div>
            </div>

            {/* Мобільна панель деталей */}
            <Drawer
                visible={detailsVisible && !isDesktop}
                onHide={() => setDetailsVisible(false)}
                title={selectedSale ? `Замовлення №${selectedSale.id}` : 'Замовлення'}
                flush
                footer={detailsActions}
            >
                {detailsContent}
            </Drawer>

            {/* Оплата */}
            <Drawer
                visible={paymentVisible}
                onHide={() => setPaymentVisible(false)}
                title="Оплата замовлення"
                footer={
                    <Button
                        label="Провести оплату"
                        icon="pi pi-check"
                        className="w-full"
                        loading={busy}
                        disabled={!payments.length}
                        onClick={submitPayment}
                    />
                }
            >
                <Message className="rc-message" severity="info" text={discountLabel()} />
                <UndistributedMessage amount={undistributed} />
                <PaymentEditor
                    payments={payments}
                    onChange={setPayments}
                    errors={paymentErrors}
                />
            </Drawer>
        </>
    );
};

export default OpenSales;
